<script lang="ts">
import Vue, { PropType } from 'vue'
import moment from 'moment'
import BannerIconShootingType from '@/components/common/organims/BannerIconShootingType.vue'
import ShootingItemContent from '@/components/shootings/ShootingItemContent.vue'
import ShootingStatus from '@/components/shootings/ShootingStatus.vue'

type ShootingType = 'STANDARD' | 'INSPECTION' | 'VIDEO' | 'INSPECTION_REPAIR_ORDER'

enum ShootingRoutesEnum {
  INSPECTION = '/inspect',
  VIDEO = '/video',
  INSPECTION_REPAIR_ORDER = '/inspection-repair-order'
}

interface Stamp {
  name: string
}

interface Shooting {
  ownerId: string
  productId: string
  shootingId: string
  shootingType: ShootingType
  created: string
  image: string
  registration: string
  numberOfPictures: number
  remarketingShootingStatus?: string
  stamps?: Stamp[]
  productModel?: string
  ownerName: string
}

interface RouteLocation {
  path: string
}

export default Vue.extend({
  name: 'ShootingsItem',

  components: {
    BannerIconShootingType,
    ShootingItemContent,
    ShootingStatus
  },

  props: {
    shooting: {
      type: Object as PropType<Shooting>,
      required: true
    }
  },

  data() {
    return {
      STANDARD_PATH: `/owners/${this.shooting.ownerId}/products/${this.shooting.productId}/shootings/${this.shooting.shootingId}`
    }
  },

  methods: {
    getShootingPath(status: ShootingType): string {
      return `${this.STANDARD_PATH}${ShootingRoutesEnum[status] || ''}`
    }
  },

  computed: {
    formatDate(): string {
      return moment(this.shooting.created).format('DD/MM/YYYY')
    },
    goToShooting(): RouteLocation {
      return { path: this.getShootingPath(this.shooting.shootingType) }
    }
  }
})
</script>

<template>
  <v-card class="image-block center-card rounded-lg" min-height="390px" style="height: 100%">
    <BannerIconShootingType :type="shooting.shootingType" :count="shooting.numberOfPictures" />
    <div v-if="shooting.shootingType !== 'VIDEO'" class="img-thumbnail-block rounded-lg rounded-b-0">
      <router-link :to="goToShooting">
        <img class="img-thumbnail rounded-0" crossorigin="anonymous" :src="shooting.image" />
      </router-link>
    </div>
    <div v-else class="img-thumbnail-block">
      <router-link :to="goToShooting" style="text-decoration: none">
        <div class="video-thumbnail rounded-0">
          <v-icon size="x-large">play_arrow</v-icon>
        </div>
      </router-link>
    </div>

    <div v-if="shooting.remarketingShootingStatus">
      <ShootingStatus :status="shooting.remarketingShootingStatus" />
    </div>

    <div>
      <v-card-subtitle class="pa-0 mt-1 truncate">
        <div class="text-subtitle-1">{{ shooting.registration }}</div>
        <v-card-text class="pa-0">{{ formatDate }}</v-card-text>
      </v-card-subtitle>
    </div>
    <v-row class="text-left spacing-0 pb-1" no-gutters dense>
      <v-col cols="12">
        <ShootingItemContent
          v-if="shooting.stamps && shooting.stamps.length"
          :title="$t('shootings.appliedStamp')"
          :content="shooting.stamps[0].name"
        />
      </v-col>
      <v-col cols="12" v-if="shooting.productModel">
        <ShootingItemContent :title="$t('shooting.details.model')" :content="shooting.productModel" />
      </v-col>
      <v-col cols="12">
        <ShootingItemContent :title="$t('shooting.details.stock')" :content="shooting.ownerName" />
      </v-col>
    </v-row>
  </v-card>
</template>

<style lang="scss" scoped>
.img-thumbnail-block {
  overflow: hidden;
  height: 150px;
}

.video-thumbnail {
  cursor: pointer;
  transition: 0.2s transform ease-in-out;
  background-color: #ebebeb;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.img-thumbnail {
  cursor: pointer;
  transition: 0.2s transform ease-in-out;
  background-color: #ebebeb;
  width: 100%;
  height: auto;
  object-fit: 'fill';
}

.image-description {
  text-align: center;
}

.image-block:hover .img-thumbnail {
  transform: scale(1.1);
}

.center-card {
  text-align: center;
}

.custom-height {
  height: 38px;
  padding-bottom: 16px;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
